// Dynamic base URLs based on environment
// In production: chat.jurismart.co calls jurismart.co API
// In development: localhost:5173 calls through Vite proxy → localhost:8000
//   → relative URLs so the Vite proxy handles CORS + CSRF seamlessly

const isDev = import.meta.env.DEV;

// Dev: relative URLs go through Vite proxy (vite.config.ts → target: http://127.0.0.1:8000)
// Prod: absolute URLs to jurismart.co
const API_BASE = isDev 
  ? '/api_agent/api/v2'
  : 'https://jurismart.co/api_agent/api/v2';

// Dev: ws:// URL is resolved by Vite proxy (ws: true in vite.config.ts)
// Prod: wss://
const WS_BASE = isDev 
  ? (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host
  : 'wss://jurismart.co';

// Auth endpoint - checks if user is authenticated and returns user info
export const AUTH_ENDPOINT = `${API_BASE}/session/`;

// CSRF token endpoint - gets CSRF token for POST/PATCH/DELETE requests
export const CSRF_ENDPOINT = `${API_BASE}/csrf/`;

// Chat CRUD endpoints
export const CREATE_CHAT_ENDPOINT = `${API_BASE}/chats/`;

export function getChatMessagesUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/messages/`;
}

// List all chats grouped by time period
export const LIST_CHATS_ENDPOINT = `${API_BASE}/list_chats/`;

// Global chat search: coincidencias en mensajes (usuario + IA) y títulos.
export function getSearchChatsUrl(query: string): string {
  const params = new URLSearchParams({ q: query });
  return `${API_BASE}/chats/search/?${params.toString()}`;
}

// ── Nivel de análisis (thinking effort) ──────────────────────
// El default global y el modelo activo se administran en el admin de Django;
// estas rutas solo leen el catálogo y guardan la preferencia de cada chat.

/** GET default global + catálogo de niveles (low/high/max). */
export const AI_PREFERENCES_ENDPOINT = `${API_BASE}/ai-preferences/`;

/** GET/PATCH el nivel de análisis elegido para un chat. */
export function getChatPreferencesUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/preferences/`;
}

/** GET Open Graph metadata and the favicon extracted from a public source URL. */
export function getLinkPreviewUrl(url: string): string {
  const params = new URLSearchParams({ url });
  return `${API_BASE}/link-preview/?${params.toString()}`;
}

// Chat management endpoints (pin, rename, delete)
export function getPinChatUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/pin/`;
}

export function getRenameChatUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/`;
}

export function getDeleteChatUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/delete/`;
}

// WebSocket for real-time chat messaging
export const MESSAGES_WS = `${WS_BASE}/ws/chat/`;

// Redirect URL when user is not authenticated
// Dev: goes through Vite proxy to Django (so cookies/CSRF work on same origin)
export const LOGIN_REDIRECT_URL = isDev 
  ? '/accounts/login/'
  : 'https://jurismart.co/accounts/login/';

export const MAIN_URL = isDev 
  ? '/'
  : 'https://jurismart.co';

export const PLANS_URL = isDev 
  ? '/subscriptions/'
  : 'https://jurismart.co/subscriptions/';

export const LOGOUT_URL = isDev 
  ? '/logout/'
  : 'https://jurismart.co/logout/';

// ── Per‑Chat Document Vector Store ─────────────────────────────
// Documents uploaded via REST (multipart/form-data) are OCR'd,
// chunked, and embedded with Ollama for semantic search within the chat.

/** POST multipart/form-data (field: "file"). Returns ChatDocument. */
export function getUploadDocumentUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/documents/`;
}

/** Create S3 direct-upload records and presigned POSTs for a batch. */
export function getPresignDocumentsUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/documents/presign/`;
}

/** Verify uploaded S3 objects and enqueue their OCR jobs. */
export function getCompleteDocumentsUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/documents/complete/`;
}

/** GET list of documents with statuses for a chat. */
export function getListDocumentsUrl(chatId: string): string {
  return `${API_BASE}/chats/${chatId}/documents/list/`;
}

/** DELETE a document and its vector chunks. Returns 204. */
export function getDeleteDocumentUrl(chatId: string, docId: number): string {
  return `${API_BASE}/chats/${chatId}/documents/${docId}/`;
}

// ── Biblioteca jurídica ──────────────────────────────────────
// Keep production requests on jurismart.co: the chat SPA is hosted on a
// different subdomain. In development these stay relative for Vite's narrow
// Biblioteca proxies.
const BIBLIOTECA_BASE = isDev
  ? '/biblioteca'
  : 'https://jurismart.co/biblioteca';

const BIBLIOTECA_API_BASE = `${BIBLIOTECA_BASE}/api`;

/** GET paginated Biblioteca documents. Query parameters are added by the feature API. */
export const BIBLIOTECA_LIST_ENDPOINT = `${BIBLIOTECA_API_BASE}/documentos/`;

/** GET the complete text and metadata for one Biblioteca document. */
export function getBibliotecaDetailUrl(documentId: number): string {
  return `${BIBLIOTECA_API_BASE}/documentos/${documentId}/`;
}

/** Normal browser navigation endpoint for generating/downloading a Biblioteca PDF. */
export function getBibliotecaPdfUrl(documentId: number): string {
  return `${BIBLIOTECA_BASE}/${documentId}/pdf/`;
}

// ── Seguimiento judicial ─────────────────────────────────────
// Igual que Biblioteca: en producción las APIs viven en jurismart.co y la
// SPA en chat.jurismart.co; en desarrollo estas rutas pasan por el proxy
// acotado de Vite.
const SEGUIMIENTO_BASE = isDev
  ? '/seguimiento-judicial'
  : 'https://jurismart.co/seguimiento-judicial';

const SEGUIMIENTO_API_BASE = `${SEGUIMIENTO_BASE}/api`;

/** GET listado + POST creación de seguimientos. */
export const SEGUIMIENTO_SEGUIMIENTOS_ENDPOINT = `${SEGUIMIENTO_API_BASE}/seguimientos/`;

/** GET detalle, PATCH preferencias y DELETE de un seguimiento. */
export function getSeguimientoDetailUrl(seguimientoId: number): string {
  return `${SEGUIMIENTO_API_BASE}/seguimientos/${seguimientoId}/`;
}

/** POST revisión manual de un seguimiento. */
export function getSeguimientoRevisarUrl(seguimientoId: number): string {
  return `${SEGUIMIENTO_API_BASE}/seguimientos/${seguimientoId}/revisar/`;
}

/** POST para seguir por radicado un proceso hallado en un nombre vigilado. */
export function getSeguirProcesoUrl(seguimientoId: number, procesoId: number): string {
  return `${SEGUIMIENTO_API_BASE}/seguimientos/${seguimientoId}/procesos/${procesoId}/seguir/`;
}

/** POST inicia una consulta progresiva (nombre o radicado). */
export const SEGUIMIENTO_CONSULTA_INICIAR_URL = `${SEGUIMIENTO_API_BASE}/consulta/iniciar/`;

/** GET progreso de una consulta progresiva. */
export function getConsultaEstadoUrl(consultaId: number): string {
  const parameters = new URLSearchParams({ id: String(consultaId) });
  return `${SEGUIMIENTO_API_BASE}/consulta/estado/?${parameters.toString()}`;
}

/** GET página de resultados ya guardados (20 por página). */
export function getConsultaResultadosUrl(
  consultaId: number,
  pagina = 1,
  orden: 'estable' | 'actuacion' = 'estable',
): string {
  const parameters = new URLSearchParams({
    id: String(consultaId),
    pagina: String(pagina),
    orden,
  });
  return `${SEGUIMIENTO_API_BASE}/consulta/resultados/?${parameters.toString()}`;
}

/** POST reanuda una consulta interrumpida. */
export const SEGUIMIENTO_CONSULTA_REINTENTAR_URL = `${SEGUIMIENTO_API_BASE}/consulta/reintentar/`;

/** POST activa (o deja pendiente) un seguimiento desde una consulta. */
export const SEGUIMIENTO_CONSULTA_ACTIVAR_URL = `${SEGUIMIENTO_API_BASE}/consulta/activar/`;

/** GET última consulta no vencida del usuario (recuperación tras recargar). */
export function getConsultaRecienteUrl(tipo?: 'radicado' | 'nombre'): string {
  if (!tipo) return `${SEGUIMIENTO_API_BASE}/consulta/reciente/`;
  const parameters = new URLSearchParams({ tipo });
  return `${SEGUIMIENTO_API_BASE}/consulta/reciente/?${parameters.toString()}`;
}

/** GET actuaciones de un proceso para expandir la vista previa (no crea nada). */
export function getConsultaProcesoUrl(idProceso: number, pagina = 1): string {
  const parameters = new URLSearchParams({
    id_proceso: String(idProceso),
    pagina: String(pagina),
  });
  return `${SEGUIMIENTO_API_BASE}/consulta/proceso/?${parameters.toString()}`;
}

/** GET historial paginado de novedades del usuario. */
export function getNovedadesUrl(pagina = 1): string {
  const parameters = new URLSearchParams({ pagina: String(pagina) });
  return `${SEGUIMIENTO_API_BASE}/novedades/?${parameters.toString()}`;
}

/** GET detalle de un proceso con sus actuaciones paginadas. */
export function getProcesoDetalleUrl(procesoId: number, pagina = 1): string {
  const parameters = new URLSearchParams({ pagina: String(pagina) });
  return `${SEGUIMIENTO_API_BASE}/procesos/${procesoId}/?${parameters.toString()}`;
}
